import { useI18n } from '@i18n/I18nProvider'
import { formatKm } from '@i18n/locale'
import { directionsUrl } from '@lib/directions'
import { BottomSheet } from '@ui/BottomSheet'
import { buttonClasses } from '@ui/Button'
import { ExternalLink } from '@ui/ExternalLink'
import { Icon } from '@ui/Icon'
import { useState } from 'react'
import { Link } from 'wouter'
import { type StagePosition, stageStart } from './stage-route'

type Props = {
	position: StagePosition
	trees: number
	pois: number
	onChoose: (id: string | null) => void
}

const ROUND = 'flex size-12 shrink-0 items-center justify-center rounded-full'

// Detalles de la etapa elegida, como una ruta a seguir: cuánto mide y qué hay en ella; pasar a
// la anterior o la siguiente, abrir su ficha o ir a la salida. Va dentro de una hoja inferior:
// el mapa queda despejado mientras no se pidan.
export const StageDetails = ({ position, trees, pois, onChoose }: Props) => {
	const { t, locale } = useI18n()
	const { stage, total, prev, next } = position
	const start = stageStart(stage)
	const directions = start && directionsUrl(start)

	return (
		<div aria-live="polite">
			<p className="font-semibold text-earth-800">
				{t('map.stageOf', { n: stage.order, total })} ·{' '}
				{t('stages.distance', { km: formatKm(stage.distanceKm, locale) })}
			</p>
			<p className="mt-1 text-earth-800">{t('map.stageCounts', { pois, trees })}</p>
			<div className="mt-4 flex items-center gap-2">
				<button
					type="button"
					onClick={() => prev && onChoose(prev.id)}
					disabled={!prev}
					aria-label={t('map.prevStage')}
					className={`${ROUND} border-2 border-forest-700 text-forest-800 disabled:opacity-40`}
				>
					<Icon name="back" />
				</button>
				<Link href={`/etapas/${encodeURIComponent(stage.id)}`} className={`${buttonClasses('primary')} flex-1`}>
					<Icon name="route" />
					{t('map.viewStage')}
				</Link>
				<button
					type="button"
					onClick={() => next && onChoose(next.id)}
					disabled={!next}
					aria-label={t('map.nextStage')}
					className={`${ROUND} border-2 border-forest-700 text-forest-800 disabled:opacity-40`}
				>
					<Icon name="forward" />
				</button>
			</div>
			{directions && (
				<div className="mt-2 text-center">
					<ExternalLink href={directions}>{t('map.toStart')}</ExternalLink>
				</div>
			)}
		</div>
	)
}

// Botón pequeño junto a la etapa elegida que abre sus detalles en una hoja inferior. Al pasar a
// la anterior o la siguiente la hoja sigue abierta y muestra la nueva.
export const StageDetailsButton = (props: Props) => {
	const { t } = useI18n()
	const [open, setOpen] = useState(false)
	return (
		<>
			<button
				type="button"
				aria-label={t('map.stageDetails')}
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={() => setOpen(true)}
				className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white text-forest-800 shadow-md active:bg-earth-100"
			>
				<Icon name="list" />
			</button>
			{open && (
				<BottomSheet title={props.position.stage.name} onClose={() => setOpen(false)}>
					<StageDetails {...props} />
				</BottomSheet>
			)}
		</>
	)
}
